import { z } from 'zod';
import { listingValues, numberParameter, overrideZoom, overrideZoomParameter, psfComputationParameters, psfComputationSettings, psfRowChanges, psfRowDescription, psfRowParameters, psfRowValues, switchParameter } from './shared.js';

const encircledCentering = z.enum(['minimum', 'chief', 'centroid']);
const encircledCenteringOptions = [
  { value: 'minimum', label: 'Minimum diameter' }, { value: 'chief', label: 'Chief ray' }, { value: 'centroid', label: 'Centroid' },
];
const encircledRowValues = { ...psfRowValues, centering: encircledCentering.optional() };
// ENC without CHI/CEN selects minimum diameter; requiring centering with
// enabled avoids silently resetting an earlier mode.
const encircledRow = z.union([
  ...psfRowChanges.map(change => z.strictObject({ ...encircledRowValues, ...change })),
  z.strictObject({ ...encircledRowValues, centering: encircledCentering, enabled: z.enum(listingValues).optional() }),
]);
// PromptingGuide pp152–153. CODE V 2026 reports XMA/XDE as obsolete (capture
// plot-bounds-with-warning), so they are not presented as effective ENC controls.
const encircledSettings = z.strictObject({
  ...psfComputationSettings,
  centering: encircledCentering.default('minimum').describe('ENC: minimum shifts the circle in Y to minimize its diameter; chief uses CHI and centroid uses CEN. Minimum is the native unqualified ENC mode (PromptingGuide p153).'),
  option_settings: z.array(encircledRow).default([]).describe(`${psfRowDescription} A centering override enables ENC unless enabled is explicitly No; enabled requires explicit centering (PromptingGuide pp152–153).`),
  // PromptingGuide p153 and HIDD_PSFPAN3 give encircled_% no type; CODE V 2026
  // Update 0.1 refuses a fractional one with "Invalid data - expecting integer
  // data" (encircled-energy-2026-update-0-1/fractional-per).
  percentages: z.array(z.strictObject({ zoom: overrideZoom, percent: z.number().int() })).max(10).default([]).describe('Ordered PER requests, up to 10 commands. Empty retains native 10,20,…,90 percent (PromptingGuide p153). CODE V may return an unavailable diameter; it is not inferred from the curve.'),
});
export default {
  label: 'Diffraction Encircled Energy', group: 'performance',
  description: 'Diffraction encircled-energy curves, and the diameters that enclose each requested energy percentage.',
  settingsSchema: encircledSettings,
  parameters: [
    ...psfComputationParameters,
    { key: 'centering', label: 'Centering', kind: 'select', options: encircledCenteringOptions },
    { key: 'option_settings', label: 'Options', kind: 'rows', columns: [
      ...psfRowParameters,
      { key: 'centering', label: 'Centering', kind: 'select', options: encircledCenteringOptions,
        omit: { first: 'Required when enabled', later: 'Required when enabled' } },
      { ...switchParameter('enabled', 'Encircled energy'), omit: { first: 'Yes with centering', later: 'Yes with centering' } },
    ] },
    { key: 'percentages', label: 'Energy percentages', kind: 'rows', columns: [
      overrideZoomParameter, numberParameter('percent', 'Energy', { unit: '%', integer: true }),
    ] },
  ],
  coverage: 'Listing settings affect only the text output. Phase and detector energy are separate analyses.',
} as const;
